import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ApiError, useAgentsApi } from '../apis/agents-apis';
import { Agent } from '../types';
import { useToast } from "../components/ui/Toast";
import { Spinner } from "../components/ui/Spinner";
import { Card, CardContent } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { ArrowLeft, Pencil } from 'lucide-react';
import AgentForm, { AgentFormRef } from './AgentForm';
import { NavigationWarningDialog } from '../components/NavigationWarningDialog';
import { useNavigation } from '../contexts/NavigationContext';
import { useAuth } from '../contexts/AuthContext';

// Define a more specific type for the location state
type LocationState = {
  startEditing?: boolean;
  from?: string;
};

export const EditAgentPage: React.FC = () => {
  const formRef = useRef<AgentFormRef>(null);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToast();
  const agentsApi = useAgentsApi();
  const [editedAgent, setEditedAgent] = useState<Agent | null>(null);
  const [originalAgent, setOriginalAgent] = useState<Agent | null>(null);
  const [isReadOnly, setIsReadOnly] = useState(() => {
    return !(location.state as { startEditing?: boolean })?.startEditing;
  });
  const initialFetchMade = useRef(false);
  const [existingAgentNames, setExistingAgentNames] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const backButtonClicked = useRef(false);
  const historyStateAdded = useRef(false);
  const { user } = useAuth();
  const { 
    showNavigationWarning, 
    setShowNavigationWarning,
    pendingNavigation,
    setPendingNavigation
  } = useNavigation();
  
  const hasUnsavedChanges = !isReadOnly && 
    JSON.stringify(editedAgent) !== JSON.stringify(originalAgent);

  // Handle browser back button and page refresh
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
        event.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  // Set up block for browser back button
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      if (hasUnsavedChanges && !backButtonClicked.current) {
        setShowNavigationWarning(true);
        setPendingNavigation(null);
        // Push the state back to keep the user on the page
        window.history.pushState(null, '', window.location.pathname);
        event.preventDefault();
      } else {
        backButtonClicked.current = false;
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [hasUnsavedChanges, setShowNavigationWarning, setPendingNavigation]);

  // Add history state when starting to edit
  useEffect(() => {
    if (!isReadOnly && !historyStateAdded.current) {
      window.history.pushState(null, '', window.location.pathname);
      historyStateAdded.current = true;
    }
  }, [isReadOnly]);

  // Handle navigation when user confirms
  useEffect(() => {
    if (pendingNavigation) {
      backButtonClicked.current = true;
      navigate(pendingNavigation);
    }
  }, [pendingNavigation, navigate]);

  const fetchAgent = useCallback(async () => {
    if (!id || initialFetchMade.current) return;
    
    try {
      initialFetchMade.current = true;
      
      // Fetch the agent data
      const agentData = await agentsApi.getAgent(id);
      setEditedAgent(agentData);
      setOriginalAgent(JSON.parse(JSON.stringify(agentData)));
      
      // Fetch all agents to check for name conflicts
      const allAgents = await agentsApi.getAgents();
      // Store just the agent names for validation
      setExistingAgentNames(allAgents.map(a => a.agentName).filter(Boolean) as string[]);
      
    } catch (error) {
      console.error('Error fetching agent:', error);
      addToast({
        title: 'Error',
        description: 'Failed to load agent. Please try again.',
        variant: 'destructive',
      });
      navigate('/studio/agents');
    }
  }, [id, agentsApi, addToast, navigate]);

  useEffect(() => {
    fetchAgent();
  }, [fetchAgent]);

  const handleSave = async () => {
    if (!editedAgent) return;
    
    try {
      setIsSaving(true);
      
      // Validate required fields
      if (!editedAgent.agentName?.trim()) {
        addToast({
          title: 'Error',
          description: 'Agent name is required',
          variant: 'destructive',
        });
        return;
      }
      
      // Check if the agent name already exists (excluding the current agent)
      if (existingAgentNames.some(name => 
        name.toLowerCase() === editedAgent.agentName?.toLowerCase() && 
        name.toLowerCase() !== originalAgent?.agentName?.toLowerCase()
      )) {
        addToast({
          title: 'Error',
          description: 'An agent with this name already exists',
          variant: 'destructive',
        });
        return;
      }
      
      // Save the agent
      await agentsApi.updateAgent(editedAgent);
      
      // Update the original agent to match the saved state
      setOriginalAgent(JSON.parse(JSON.stringify(editedAgent)));
      setIsReadOnly(true);
      
      addToast({
        title: 'Success',
        description: 'Agent saved successfully',
        variant: 'default',
      });
      
      // If there's a pending navigation, execute it
      if (pendingNavigation) {
        navigate(pendingNavigation);
        setPendingNavigation(null);
      }
      
    } catch (error) {
      console.error('Error saving agent:', error);
      addToast({
        title: 'Error',
        description: 'Failed to save agent. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmNavigation = () => {
    setShowNavigationWarning(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  };

  const handleCancelNavigation = () => {
    setShowNavigationWarning(false);
    setPendingNavigation(null);
  };

  const checkUnsavedChanges = useCallback((to: string) => {
    if (hasUnsavedChanges) {
      setShowNavigationWarning(true);
      setPendingNavigation(to);
      return false;
    }
    return true;
  }, [hasUnsavedChanges, setShowNavigationWarning, setPendingNavigation]);

  const handleDiscardChanges = () => {
    if (hasUnsavedChanges) {
      setShowNavigationWarning(true);
      setPendingNavigation('/studio/agents');
    } else {
      setEditedAgent(originalAgent);
      setIsReadOnly(true);
    }
  };

  const handleNavigateBack = () => {
    navigate(-1);
  };

  if (!editedAgent && initialFetchMade.current) {
    return (
      <div className="container mx-auto p-8">
        <div className="text-center py-12">
          <p className="text-gray-500">Agent not found</p>
          <Button 
            variant="outline" 
            className="mt-4"
            onClick={() => navigate('/studio/agents')}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Agents
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-4" data-has-unsaved-changes={hasUnsavedChanges}>
      {/* Back Button - Positioned absolutely */}
      <div className="absolute left-4 top-4">
        <Button
          variant="ghost"
          onClick={() => {
            if (hasUnsavedChanges) {
              setShowNavigationWarning(true);
              setPendingNavigation('/studio/agents');
            } else {
              navigate('/studio/agents');
            }
          }}
          size="sm"
          className="px-2 sm:px-3"
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="sr-only sm:not-sr-only sm:ml-2">Back</span>
        </Button>
      </div>

      {/* Main Content */}
      <div className="pt-10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div className="flex-1 min-w-0">
            <div className="overflow-hidden">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {isReadOnly ? 'View Agent' : 'Edit Agent'}
              </h1>
              <p className="text-sm font-medium text-gray-500 text-muted-foreground mt-1">
                {isReadOnly 
                  ? 'View and manage this agent configuration.'
                  : 'Update the agent details and configuration.'}
              </p>
            </div>
          </div>

          {isReadOnly && (
            <div className="mt-4 sm:mt-0">
              <Button 
                onClick={() => setIsReadOnly(false)}
                size="sm"
                className="w-full sm:w-auto"
              >
                <Pencil className="mr-2 h-4 w-4" />
                <span>Edit Agent</span>
              </Button>
            </div>
          )}
        </div>
      </div>

      {!editedAgent ? (
        <div className="flex justify-center items-center h-64">
          <Spinner className="h-8 w-8" />
        </div>
      ) : (
        <AgentForm
          ref={formRef}
          agent={editedAgent}
          originalAgent={originalAgent}
          onAgentChange={setEditedAgent}
          onSubmit={handleSave}
          onCancel={() => setIsReadOnly(true)}
          isEditing={true}
          readOnly={isReadOnly}
          onEditClick={() => setIsReadOnly(false)}
          onDiscardChanges={handleDiscardChanges}
          existingAgents={existingAgentNames.map(name => ({
            agentId: '',
            agentName: name,
            agentDescription: '',
            agentPrompt: '',
            agentSelectedTools: [],
            agentTags: [],
            tags: [],
            isCore: false,
            accessControl: {
              viewAccessType: 'Owner Only',
              viewAllowedUsers: [],
              editAccessType: 'Owner Only',
              editAllowedUsers: []
            },
            ownerId: ''
          }))}
          isSaving={isSaving}
          hasUnsavedChanges={hasUnsavedChanges}
          setIsReadOnly={setIsReadOnly}
        />
      )}
      
      <NavigationWarningDialog
        open={showNavigationWarning}
        onConfirm={handleConfirmNavigation}
        onCancel={handleCancelNavigation}
        title="Discard Changes?"
        description="You have unsaved changes that will be lost if you leave this page. Are you sure you want to discard them?"
      />
    </div>
  );
};

export default EditAgentPage;